import React, { useEffect, useState } from 'react';
import { adminApi } from './api';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Users, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '../lib/logger';

const STATUS_COLOR = {
  signed_up: 'border-foreground/20 text-foreground/70',
  converted: 'border-[#004C54]/40 text-[#004C54] bg-[#F0F4F4]',
  credited: 'border-[#4FBCC7]/40 text-[#0a0d0e] bg-[#E6F4F6]',
  rejected: 'border-destructive/30 text-destructive',
};

export default function AdminReferrals() {
  const [items, setItems] = useState(null);

  const refresh = async () => {
    try {
      const { referrals } = await adminApi.listReferrals();
      setItems(referrals || []);
    } catch (e) {
      logger.error('admin referrals failed', e);
      toast.error(e.message || 'Could not load referrals.');
      setItems([]);
    }
  };

  useEffect(() => { refresh(); }, []);

  const updateStatus = async (referral_id, status) => {
    try {
      await adminApi.updateReferral(referral_id, { status });
      setItems((prev) => prev.map((r) => r.referral_id === referral_id ? { ...r, status } : r));
      toast.success(`Marked as ${status}.`);
    } catch (e) { toast.error(e.message || 'Could not update.'); }
  };

  if (items === null) {
    return (
      <div className="space-y-2" data-testid="admin-referrals-loading">
        {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-referrals-page">
      <div className="flex items-center gap-2">
        <Users className="h-4 w-4 text-[#004C54]" />
        <h2 className="font-heading text-lg font-bold">Referrals</h2>
        <Badge variant="outline" className="border-foreground/15 text-foreground/60">{items.length}</Badge>
      </div>

      {items.length === 0 ? (
        <Card className="border-border"><CardContent className="p-6 text-center text-foreground/60" data-testid="admin-referrals-empty">
          No referrals yet. Share your link from the dashboard to start growing.
        </CardContent></Card>
      ) : (
        <Card className="border-border overflow-hidden">
          <div className="hidden sm:grid grid-cols-[1fr_1fr_1fr_140px_auto] gap-3 px-4 py-2 bg-[#F6F7F8] text-[11px] uppercase tracking-wider text-foreground/60 font-semibold border-b border-border">
            <div>Referrer</div>
            <div>Referee</div>
            <div>Code / Date</div>
            <div>Status</div>
            <div>Action</div>
          </div>
          <ul className="divide-y divide-border" data-testid="admin-referrals-list">
            {items.map((r) => (
              <li key={r.referral_id} className="px-4 py-3 grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_140px_auto] gap-2 sm:gap-3 items-center">
                <div>
                  <div className="text-[13px] font-semibold truncate">{r.referrer_name || '—'}</div>
                  <div className="text-[11px] text-foreground/55 truncate">{r.referrer_email || ''}</div>
                </div>
                <div>
                  <div className="text-[13px] truncate">{r.referee_email}</div>
                </div>
                <div>
                  <div className="font-mono text-[12px]">{r.code}</div>
                  <div className="text-[11px] text-foreground/55">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}</div>
                </div>
                <div>
                  <Badge variant="outline" className={STATUS_COLOR[r.status] || 'border-foreground/15 text-foreground/70'}>
                    {r.status}
                  </Badge>
                </div>
                <div className="flex gap-1.5">
                  {r.status !== 'converted' && (
                    <Button size="sm" variant="outline" onClick={() => updateStatus(r.referral_id, 'converted')} data-testid={`referral-convert-${r.referral_id}`}>
                      Mark converted
                    </Button>
                  )}
                  {r.status === 'converted' && (
                    <Button size="sm" variant="outline" onClick={() => updateStatus(r.referral_id, 'credited')} data-testid={`referral-credit-${r.referral_id}`}>
                      <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Credit
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
